// Pure grid geometry for the Bento dashboard canvas. The app's own state
// (saved layouts) lives in DynamoDB; this is the framework-free maths that both
// the frontend (placing widgets) and the backend (validating a saved layout on
// write) run, so it lives in @catbento/shared with no AWS/DOM deps.

/** A widget's placement on the fixed-column canvas grid (grid cells, not px). */
export interface GridPosition {
  x: number;
  y: number;
  /** Width in columns (>= 1). */
  w: number;
  /** Height in rows (>= 1). */
  h: number;
}

/** One widget placed on a saved board. */
export interface WidgetPlacement {
  widgetKey: string;
  position: GridPosition;
}

/**
 * Clamp a placement so it fits inside a grid of `columns` columns: width is
 * capped to the grid, x is pulled back so `x + w` never overflows, and x/y/w/h
 * are floored to whole cells and kept non-negative. Returns a new object — the
 * input is not mutated.
 *
 * Why: drag/resize and untrusted saved layouts can produce out-of-bounds or
 * fractional positions; every placement passes through here so the rest of the
 * app can assume a valid, in-bounds position (fail-safe geometry, not a
 * fallback for a real error).
 */
export function clampToGrid(pos: GridPosition, columns: number): GridPosition {
  const w = Math.min(Math.max(1, Math.floor(pos.w)), columns);
  const h = Math.max(1, Math.floor(pos.h));
  const maxX = columns - w;
  const x = Math.min(Math.max(0, Math.floor(pos.x)), maxX);
  const y = Math.max(0, Math.floor(pos.y));
  return { x, y, w, h };
}
